import React, { useState, useEffect } from "react";
import styled from "styled-components";
import ModalBasic from "./ModalBasic.jsx";
import celebrationPopup from "../../img/pc_signup_celebration_popup.png";
import closeButton from "../../img/close_button.png";
import useStore from "../../store.js";

const ModalFriendInvite = ({ isOpen, onClose }) => {
  const [isMobile, setIsMobile] = useState(false);
  const { referralCode } = useStore();
  
  // 초대 링크 생성
  const inviteLink = referralCode 
    ? `https://lawlow.info/invite?code=${referralCode}`
    : "초대 링크를 생성하는 중입니다...";
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  // 화면 크기 감지
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 430);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // 토스트 알림 표시
  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 1000); // 1초 후 자동 사라짐
  };

  // 초대 링크 복사
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      showToastMessage('초대 링크가 복사되었습니다!');
    } catch (err) {
      console.error('복사 실패:', err);
      showToastMessage('복사에 실패했습니다. 다시 시도해주세요.');
    }
  };

  return (
    <ModalBasic
      isOpen={isOpen}
      onClose={onClose}
      width={isMobile ? "min(312px, calc(100vw - 40px))" : "400px"}
      height={isMobile ? "422px" : "474px"}
    >
      <Container isMobile={isMobile}>
        <ImageContainer isMobile={isMobile}>
          <TopImage src={celebrationPopup} alt="친구초대" />
          <CloseButton onClick={onClose}>
            <img
              src={closeButton}
              alt="닫기 버튼"
              width="32px"
              height="32px"
            />
          </CloseButton>
        </ImageContainer>
        
        <ContentSection isMobile={isMobile}>
          <Title isMobile={isMobile}>주변 친구에게 로우로우를 알리고<br />5로우를 받아보세요!</Title>
          <HrStyles />
          <InviteSection>
            <SectionTitle>초대 링크가 생성되어있어요!</SectionTitle>
            <LinkContainer>
              <LinkText isMobile={isMobile}>{inviteLink}</LinkText>
            </LinkContainer>
            <CopyButton onClick={handleCopyLink}>
              초대 링크 복사하기
            </CopyButton>
          </InviteSection>
        </ContentSection>
      </Container>
      
      {/* 토스트 알림 */}
      <Toast show={showToast} isMobile={isMobile}>
        {toastMessage}
      </Toast>
    </ModalBasic>
  );
};

// 스타일 컴포넌트들
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: ${props => props.isMobile ? '152px' : '195px'};
`;

const TopImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px 12px 0 0;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 10;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ContentSection = styled.div`
  padding: ${props => props.isMobile ? '12px 24px 24px' : '20px 32px 24px 32px'};
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Title = styled.h2`
  margin: 0 0 20px 0;
  text-align: center;
  color: #000;
  font-family: Pretendard;
  font-size: ${props => props.isMobile ? '16px' : '18px'};
  font-weight: 700;
  line-height: ${props => props.isMobile ? '26px' : '28px'};
  letter-spacing: -0.36px;
`;

const HrStyles = styled.hr`
  flex-shrink: 0;
  border-top: 0.5px rgba(0, 0, 0, 0.1);
  white-space: normal; /* 텍스트가 가로 너비를 넘어갈 때 자동 줄바꿈 */

  width: 100%;
  margin: 0 0 20px 0;
`;

const InviteSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionTitle = styled.p`
  margin: 0;
  color: rgba(0, 0, 0, 0.3);
  text-align: center;
  font-style: normal;
  font-size: 14px;
  line-height: 24px;
  font-weight: 500;
  letter-spacing: -0.28p;

  @media (max-width: 430px) {
    font-size: 12px;
    line-height: normal;
    letter-spacing: -0.24px;
  }
`;

const LinkContainer = styled.div`
  background: #F8F9FA;
  border: 1px solid #E9ECEF;
  border-radius: 8px;
  padding: 8px 15.27px;
  text-align: center;

  @media (max-width: 430px) {
    padding: 5px 32px;
  }
`;

const LinkText = styled.span`
  color: rgba(0, 0, 0, 0.3);
  font-size: ${props => props.isMobile ? '14px' : '16px'};
  font-style: normal;
  font-weight: 500;
  line-height: 30px; 
  letter-spacing: ${props => props.isMobile ? '-0.28px' : '-0.32px'};
  word-break: break-all;
`;

const CopyButton = styled.button`
  width: 100%;
  padding: 8px 15.27px;
  background: #062515;
  border: none;
  border-radius: 8px;
  color: #00F370;
  font-size: 16px;
  font-weight: 600;
  line-height: 30px;
  letter-spacing: -0.32px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #0a3520;
  }

  @media (max-width: 430px) {
    padding: 5px 15.27px;
    font-size: 14px;
    letter-spacing: -0.28px;
  }
`;

const Toast = styled.div`
  position: fixed;
  bottom: ${props => props.isMobile ? '80px' : '100px'};
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;
  z-index: 1000;
  opacity: ${props => props.show ? 1 : 0};
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  white-space: nowrap;
  
  @media (max-width: 430px) {
    font-size: 12px;
    padding: 10px 20px;
    bottom: 60px;
  }
`;

export default ModalFriendInvite;

